import { cva, type VariantProps } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "./cn";
import { Icon } from "./icon";

const alertVariants = cva("flex gap-2.5 rounded-md border p-3 text-cell", {
  variants: {
    tone: {
      info: "border-info/40 bg-info-soft text-info-fg",
      success: "border-success/40 bg-success-soft text-success-fg",
      warning: "border-warning/50 bg-warning-soft text-warning-fg",
      danger: "border-danger/40 bg-danger-soft text-danger-fg",
    },
  },
  defaultVariants: { tone: "info" },
});

const ICONS = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: XCircle } as const;

interface AlertProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  title?: string;
  children?: ReactNode;
}

/** Inline notice. Colour is never the only signal: an icon and words always accompany it (§6). */
export function Alert({ className, tone = "info", title, children, ...props }: AlertProps) {
  const t = tone ?? "info";
  return (
    <div
      role={t === "danger" ? "alert" : "status"}
      className={cn(alertVariants({ tone: t }), className)}
      {...props}
    >
      <Icon icon={ICONS[t]} size={16} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className={cn(title && "mt-0.5")}>{children}</div> : null}
      </div>
    </div>
  );
}
